/**
 * Content Library — listing and create (Libraries v2).
 *
 * Parity with Studio's library tab, plus the two states Studio's list shows that a
 * simple listing would drop:
 *   has_unpublished_changes / has_unpublished_deletes
 *     A library can hold drafted DELETIONS. Publishing then removes content from every
 *     course using it, so an author must see that before they hit publish — this is the
 *     single most consequential thing on the page.
 *   can_edit_library
 *     Read-only libraries are shown, not hidden. A library you can see but not change is
 *     information; a library that silently vanishes is confusing.
 *
 * `license` is exposed on create rather than defaulted away: it is the field that
 * matters if library content is ever licensed to another academy.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  Field,
  Icon,
  SearchField,
  SelectField,
  Spinner,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { getConfig } from '../../shared/lib/config';
import { ApiError } from '../../shared/lib/openedx';
import {
  createLibrary,
  deriveSlug,
  listLibraries,
  resolveOrgs,
  studioHome,
  type StudioLibrary,
} from '../../shared/lib/studio';
import { LIBRARY_LICENCES } from '../../shared/lib/library';

import { ANONYMOUS, loadSession, personaOf } from '../../shared/lib/session';
import { PageHeader } from '../AdminShell';

/**
 * Route to a library's detail page.
 *
 * Keys look like `lib:ORG:slug`, so the colons have to be encoded — an unencoded key
 * still matches the route but arrives with its segments split in the wrong places.
 */
const libraryHref = (id: string) => `/admin/libraries/${encodeURIComponent(id)}`;

/** The platform's "all rights reserved" is the empty string, not a slug. */
const DEFAULT_LICENCE = '';

/** Mirrors Open edX's LICENSE_OPTIONS. */

export default function AdminLibrary() {
  const config = getConfig();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const home = useQuery({ queryKey: ['studio-home'], queryFn: studioHome, staleTime: 5 * 60_000 });
  const libraries = useQuery({
    queryKey: ['studio-libraries', config.tenant.slug, search],
    queryFn: () => listLibraries(config.tenant.slug, { search: search || undefined }),
  });

  // Gate on the persona this academy grants, NOT on the platform's global creator status.
  //
  // Creating a library goes through our own tenant endpoint, which checks org-scoped roles. The
  // platform's `course_creator_status` answers a different question — "may they create libraries
  // anywhere on the install?" — and it is deliberately `unrequested` for tenant admins, because
  // granting it would let them create under every organisation on the install. Gating on it hid
  // the button from precisely the people who are allowed to press it.
  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const persona = personaOf(session.data ?? ANONYMOUS, config.tenant.orgs);
  const allowedToCreate = persona === 'tenant_admin' || persona === 'tenant_staff' || persona === 'superadmin';

  const creatableOrgs = resolveOrgs(
    home.data?.allowed_organizations_for_libraries ?? [],
    config.tenant.orgs,
  );

  const columns: Column<StudioLibrary>[] = [
    {
      key: 'title',
      header: 'Library',
      render: (library) => (
        <>
          <a href={libraryHref(library.id)}>{library.title}</a>
          <div className="admin__sub">{library.id}</div>
        </>
      ),
    },
    { key: 'blocks', header: 'Blocks', render: (l) => l.num_blocks ?? 0, numeric: true },
    {
      key: 'state',
      header: 'State',
      render: (library) => (
        <div className="admin__badges">
          {library.has_unpublished_deletes && <Badge tone="danger">Unpublished deletes</Badge>}
          {library.has_unpublished_changes && !library.has_unpublished_deletes && (
            <Badge tone="warning">Unpublished changes</Badge>
          )}
          {!library.has_unpublished_changes && !library.has_unpublished_deletes && (
            <Badge tone="success">Published</Badge>
          )}
          {!library.can_edit_library && <Badge>Read only</Badge>}
        </div>
      ),
    },
    {
      key: 'published',
      header: 'Last published',
      render: (library) =>
        library.last_published
          ? new Date(library.last_published).toLocaleDateString()
          : 'Never',
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (library) => (
        <div className="admin__row-actions">
          <a className="ui-btn" data-variant="ghost" href={libraryHref(library.id)}>
            {library.can_edit_library ? 'Edit' : 'View'}
          </a>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Content Library"
        description="Reusable blocks and units that courses can pull from."
        actions={
          allowedToCreate &&
          creatableOrgs.length > 0 && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Icon name="add" />
              New library
            </Button>
          )
        }
      />

      {home.data && !home.data.libraries_v2_enabled && (
        <Banner tone="warning">
          <strong>Libraries are turned off on this platform.</strong> Enable the
          Libraries v2 waffle flag in Studio before creating any.
        </Banner>
      )}

      {/* Said once, plainly, instead of letting the button 403. Library creation is a global
          permission in Open edX, so a tenant admin who can create courses may still not be able
          to create libraries — which is surprising enough to be worth spelling out. */}
      {!allowedToCreate && !session.isLoading && (
        <Banner tone="info">
          <strong>You can use libraries here, but not create them.</strong> Creating a library
          needs staff or administrator access in this academy. An administrator can change that
          from the People page.
        </Banner>
      )}

      {creating && (
        <NewLibraryCard
          tenantSlug={config.tenant.slug}
          orgs={creatableOrgs}
          onDone={() => setCreating(false)}
        />
      )}

      <Toolbar>
        <div className="toolbar__end">
          <SearchField value={search} onChange={setSearch} placeholder="Search libraries…" />
        </div>
      </Toolbar>

      {libraries.isLoading ? (
        <Spinner label="Loading libraries…" />
      ) : libraries.isError ? (
        <Banner tone="danger">
          <strong>Could not load libraries.</strong> Studio did not respond.
        </Banner>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            rows={libraries.data ?? []}
            rowKey={(library) => library.id}
            empty="No libraries yet. Create one to share content between courses."
          />
        </Card>
      )}
    </>
  );
}

function NewLibraryCard({
  tenantSlug,
  orgs,
  onDone,
}: {
  /** Which academy this library belongs to. The server derives the org from it. */
  tenantSlug: string;
  orgs: string[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [org, setOrg] = useState(orgs[0] ?? '');
  const [license, setLicense] = useState(DEFAULT_LICENCE);
  const [advanced, setAdvanced] = useState(false);
  const [slug, setSlug] = useState('');

  const effectiveSlug = slug || deriveSlug(title);
  const key = `lib:${org}:${effectiveSlug}`;

  const create = useMutation({
    mutationFn: () =>
      // No `org` unless the tenant genuinely has more than one — the server defaults it, and a
      // client that names its own org could file a library into another academy.
      createLibrary(tenantSlug, {
        ...(orgs.length > 1 ? { org } : {}),
        slug: effectiveSlug,
        title: title.trim(),
        license,
      }),
    onSuccess: (library) => {
      queryClient.invalidateQueries({ queryKey: ['studio-libraries'] });
      window.location.assign(libraryHref(library.id));
    },
  });

  return (
    <Card title="New library">
      <form
        className="console__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) create.mutate();
        }}
      >
        <Field
          label="Library title"
          name="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          hint="What authors see when picking content to reuse."
          required
          autoFocus
        />

        {orgs.length > 1 && (
          <SelectField
            label="Organisation"
            name="org"
            value={org}
            onChange={(e) => setOrg(e.target.value)}
            options={orgs.map((o) => [o, o])}
          />
        )}

        {/* A real choice, not a default to hide: this is the field that matters if
            content is ever licensed to another academy. */}
        <SelectField
          label="Content licence"
          name="license"
          value={license}
          onChange={(e) => setLicense(e.target.value)}
          options={LIBRARY_LICENCES}
        />

        <p className="admin__derived">
          Library ID <code>{key}</code>
          <button
            type="button"
            className="ui-btn"
            data-variant="ghost"
            onClick={() => setAdvanced(!advanced)}
          >
            {advanced ? 'Hide' : 'Change'}
          </button>
        </p>

        {advanced && (
          <div className="admin__advanced">
            <Field
              label="Slug"
              name="slug"
              value={effectiveSlug}
              onChange={(e) => setSlug(e.target.value)}
              hint="Permanent. Lowercase letters, numbers and hyphens."
            />
          </div>
        )}

        {create.isError && (
          <Banner tone="danger">
            <strong>
              {create.error instanceof ApiError && create.error.status === 400
                ? 'That library ID is already taken.'
                : 'The library was not created.'}
            </strong>{' '}
            {create.error instanceof ApiError && create.error.status === 400
              ? 'Change the slug under “Change”.'
              : 'Studio rejected the request.'}
          </Banner>
        )}

        <div className="console__form-actions">
          <Button type="submit" variant="primary" busy={create.isPending} disabled={!title.trim()}>
            Create library
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
